import { useEffect, useRef } from 'react';

import { useFormikContext } from 'formik';
import { useIntl } from 'react-intl';

import { toaster } from 'components/toaster/toaster';
import { ISignUpCompleteQuery, useSignInJwtMutation } from 'generated/graphql-gateway';
import { CustomEventNames, EventTypes, autoSignInEvent, logRBIEvent } from 'state/amplitude';
import { AuthenticationPhases } from 'state/amplitude/constants';
import { useAuthContext } from 'state/auth';
import { useConfirmJwt } from 'state/auth/hooks/use-confirm-jwt';
import { platform, welcomeEmailDomain } from 'utils/environment';
import { ISignupForm } from 'utils/form/types';

interface IAutoSignin {
  signUpJwt: string;
  signUpCompleteData?: ISignUpCompleteQuery;
  stopPolling: () => void;
}

// TODO: Convert this into a hook. This doesn't render anything.
const AutoSignin: React.FC<React.PropsWithChildren<IAutoSignin>> = ({
  signUpJwt,
  signUpCompleteData,
  stopPolling,
}) => {
  const { handleConfirmJwt } = useConfirmJwt();
  const { values } = useFormikContext<ISignupForm>();
  const [signInJwtMutation] = useSignInJwtMutation();
  const { formatMessage } = useIntl();
  const {
    isAuthenticated,
    modalAuthIsOpen,
    onSignInSuccess,
    modalAuthState,
    setModalAuthState,
  } = useAuthContext();

  const hasMutatedRef = useRef(false);

  const { email } = values;

  useEffect(() => {
    if (
      !hasMutatedRef.current &&
      !isAuthenticated &&
      signUpJwt &&
      signUpCompleteData?.signUpComplete
    ) {
      // TODO: handle a loading state for the sign up form in the campaign auth flow
      stopPolling();

      autoSignInEvent({ phase: AuthenticationPhases.VALIDATION });

      signInJwtMutation({
        variables: {
          input: {
            email,
            stage: welcomeEmailDomain(),
            platform: platform(),
          },
        },
      })
        .then(async () => {
          hasMutatedRef.current = true;

          await handleConfirmJwt(signUpJwt, email);

          logRBIEvent({
            name: CustomEventNames.AUTO_SIGN_IN,
            type: EventTypes.Other,
            attributes: {
              signUpType: email ? 'Email' : 'Phone Number',
            },
          });

          autoSignInEvent({ phase: AuthenticationPhases.COMPLETE });

          toaster.positive(formatMessage({ id: 'autoSignInSuccess' }));

          onSignInSuccess();
        })
        .catch(err => {
          throw err;
        });
    }
  }, [
    handleConfirmJwt,
    stopPolling,
    signUpJwt,
    signUpCompleteData,
    email,
    signInJwtMutation,
    formatMessage,
    modalAuthIsOpen,
    isAuthenticated,
    onSignInSuccess,
    modalAuthState,
    setModalAuthState,
  ]);

  return null;
};

export default AutoSignin;
